import styled from 'styled-components';

const Wrap = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding-inline: clamp(20px, 4.5vw, 64px);
`;

export const PageWrapper = styled.div`
  background: #F3ECE1;
  min-height: 100vh;
  padding-top: 88px;
`;

export const Eyebrow = styled.span`
  display: block;
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #D9442B;
`;

export const Label = styled.span`
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

// ---------- hero ----------
export const HeroSection = styled(Wrap)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 544px);
  gap: clamp(40px, 5vw, 72px);
  align-items: start;
  padding-block: 56px 88px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    align-items: center;
  }
`;

export const HeroContent = styled.div``;

export const HeroTitle = styled.h1`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: clamp(52px, 6.2vw, 88px);
  font-weight: 400;
  line-height: 0.95;
  margin: 18px 0 0;
  max-width: 11em;
  letter-spacing: -0.02em;
  color: #1A1814;

  em {
    font-style: italic;
    color: #D9442B;
  }

  @media (max-width: 768px) {
    font-size: 48px;
    line-height: 1;
  }
`;

export const HeroSubtitle = styled.p`
  margin: 28px 0 0;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 20px;
  line-height: 1.5;
  color: #4A453D;
  max-width: 30em;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

export const PriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin-top: 36px;
`;

export const Price = styled.b`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: 64px;
  font-weight: 400;
  line-height: 1;
  letter-spacing: -0.02em;
  color: #1A1814;

  @media (max-width: 768px) {
    font-size: 52px;
  }
`;

export const OldPrice = styled.s`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: 30px;
  font-weight: 400;
  color: #8A8276;
  text-decoration-color: #D9442B;
  text-decoration-thickness: 2px;
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px 34px;
  margin-top: 28px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 17px 26px;
  background: #1A1814;
  color: #F3ECE1;
  font-family: 'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.01em;
  white-space: nowrap;
  border: none;
  cursor: pointer;
  transition: gap 0.2s ease;

  &:hover {
    gap: 18px;
  }

  &:focus-visible {
    outline: 2px solid #D9442B;
    outline-offset: 3px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const ButtonArrow = styled.span`
  color: #D9442B;
`;

export const TextLink = styled.a`
  font-family: 'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif;
  font-size: 15px;
  font-weight: 500;
  border-bottom: 1.5px solid #D9442B;
  padding-bottom: 3px;
  cursor: pointer;
  text-decoration: none;
  color: inherit;

  &:focus-visible {
    outline: 2px solid #D9442B;
    outline-offset: 3px;
  }
`;

export const SmallNote = styled.p`
  margin-top: 22px;
  color: #6B645A;
  font-size: 11px;
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

export const HeroImageWrapper = styled.div`
  position: relative;
  max-width: 544px;
  width: 100%;
  align-self: start;

  @media (max-width: 1024px) {
    max-width: 460px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    max-width: 380px;
  }
`;

export const HeroImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

// ---------- what you get ----------
export const GetSection = styled(Wrap)`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  border-top: 1px solid #1A1814;
  border-bottom: 1px solid #D2C8B8;

  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const GetItem = styled.div`
  padding: 22px 16px 24px 0;

  & + & {
    padding-left: 20px;
    border-left: 1px solid #D2C8B8;
  }

  @media (max-width: 760px) {
    border-bottom: 1px solid #D2C8B8;

    & + & {
      padding-left: 0;
      border-left: 0;
    }

    &:nth-child(2n) {
      padding-left: 16px;
      border-left: 1px solid #D2C8B8;
    }

    &:last-child {
      grid-column: 1 / -1;
      padding-left: 0;
      border-left: 0;
      border-bottom: 0;
    }
  }
`;

export const GetValue = styled.b`
  display: block;
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: 44px;
  font-weight: 400;
  line-height: 1;
  letter-spacing: -0.02em;
  color: #1A1814;
`;

export const GetLabel = styled(Label)`
  display: block;
  margin-top: 10px;
  color: #6B645A;
`;

// ---------- who it's for ----------
export const WhoSection = styled(Wrap)`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 72px;
  padding-block: 112px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 40px;
    padding-block: 72px;
  }
`;

export const WhoIntro = styled.div``;

export const WhoTitle = styled.h2`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: clamp(44px, 4.6vw, 64px);
  font-weight: 400;
  line-height: 1;
  margin: 16px 0 0;
  letter-spacing: -0.02em;
  color: #1A1814;

  em {
    font-style: italic;
    color: #D9442B;
  }

  @media (max-width: 768px) {
    font-size: 42px;
  }
`;

export const WhoCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  border-top: 1px solid #1A1814;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const WhoCol = styled.div`
  padding: 24px 28px 0 0;

  & + & {
    padding-left: 28px;
    border-left: 1px solid #D2C8B8;
  }

  @media (max-width: 760px) {
    & + & {
      padding-left: 0;
      border-left: 0;
    }
  }
`;

export const WhoColTitle = styled.h3`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: 30px;
  font-weight: 400;
  margin: 10px 0;
  color: #1A1814;
`;

export const WhoColText = styled.p`
  margin: 0;
  color: #4A453D;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 17px;
  line-height: 1.55;
`;

export const NotSection = styled.div`
  grid-column: 1 / -1;
  margin-top: 36px;
  padding-top: 22px;
  border-top: 1px solid #D2C8B8;
`;

export const NotText = styled.p`
  margin: 8px 0 0;
  color: #4A453D;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 17px;
  line-height: 1.55;
`;

// ---------- inside (dark) ----------
export const InsideSection = styled.section`
  background: #1A1814;
  color: #F3ECE1;
  padding-block: 112px;

  @media (max-width: 768px) {
    padding-block: 72px;
  }
`;

export const InsideWrap = styled(Wrap)``;

export const InsideTitle = styled.h2`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: clamp(44px, 4.6vw, 64px);
  font-weight: 400;
  line-height: 1;
  margin: 16px 0 0;
  letter-spacing: -0.02em;
  color: #F3ECE1;
  max-width: 14em;

  em {
    font-style: italic;
    color: #D9442B;
  }

  @media (max-width: 768px) {
    font-size: 42px;
  }
`;

export const InsideLead = styled.p`
  margin: 20px 0 0;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 19px;
  color: #BDB3A3;
  max-width: 34em;
  line-height: 1.55;

  @media (max-width: 768px) {
    font-size: 17px;
  }
`;

export const InsideImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  margin-top: 56px;
  border: 1px solid #3A342C;
`;

export const PartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
  margin-top: 56px;
  border-top: 1px solid #F3ECE1;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const Part = styled.div`
  padding: 28px 28px 32px 0;
  border-bottom: 1px solid #3A342C;
  display: flex;
  flex-direction: column;

  &:not(:nth-child(3n+1)) {
    padding-left: 28px;
    border-left: 1px solid #3A342C;
  }

  @media (max-width: 1100px) {
    &:not(:nth-child(3n+1)) {
      padding-left: 0;
      border-left: 0;
    }

    &:nth-child(2n) {
      padding-left: 28px;
      border-left: 1px solid #3A342C;
    }
  }

  @media (max-width: 760px) {
    &:nth-child(2n) {
      padding-left: 0;
      border-left: 0;
    }
  }
`;

export const PartNumber = styled(Label)`
  color: #D9442B;
`;

export const PartTitle = styled.h3`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: 30px;
  font-weight: 400;
  line-height: 1.05;
  margin: 10px 0 14px;
  color: #F3ECE1;
`;

export const PartList = styled.ol`
  margin: 0;
  padding-left: 22px;
  color: #BDB3A3;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 15.5px;
  line-height: 1.6;

  li::marker {
    font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
    font-size: 11px;
    font-weight: 400;
    color: #6F675C;
  }
`;

export const PartCount = styled(Label)`
  margin-top: auto;
  padding-top: 18px;
  color: #A89E8F;
`;

// ---------- toolkit ----------
export const ToolkitSection = styled(Wrap)`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 72px;
  padding-block: 112px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 40px;
    padding-block: 72px;
  }
`;

export const ToolkitImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border: 1px solid #D2C8B8;
`;

export const ToolkitContent = styled.div``;

export const ToolkitTitle = styled.h2`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: clamp(44px, 4.6vw, 64px);
  font-weight: 400;
  line-height: 1;
  margin: 16px 0 0;
  letter-spacing: -0.02em;
  color: #1A1814;

  em {
    font-style: italic;
    color: #D9442B;
  }

  @media (max-width: 768px) {
    font-size: 42px;
  }
`;

export const ToolkitLead = styled.p`
  margin: 18px 0 0;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 19px;
  color: #4A453D;
  max-width: 30em;
  line-height: 1.55;

  @media (max-width: 768px) {
    font-size: 17px;
  }
`;

export const ToolsList = styled.ol`
  list-style: none;
  margin: 36px 0 0;
  padding: 0;
  columns: 2;
  column-gap: 40px;
  border-top: 1px solid #1A1814;
  padding-top: 8px;

  @media (max-width: 760px) {
    columns: 1;
  }
`;

export const ToolItem = styled.li`
  break-inside: avoid;
  display: grid;
  grid-template-columns: 34px 1fr;
  padding: 9px 0;
  border-bottom: 1px solid #D2C8B8;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 16px;
  line-height: 1.55;
  color: #1A1814;

  span {
    font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
    font-size: 11px;
    font-weight: 400;
    color: #D9442B;
    padding-top: 4px;
  }
`;

// ---------- author ----------
export const AuthorSection = styled(Wrap)`
  border-top: 1px solid #D2C8B8;
  padding-block: 112px;
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
  gap: 72px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 40px;
    padding-block: 72px;
  }
`;

export const Quote = styled.blockquote`
  margin: 0;
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-style: italic;
  font-size: clamp(36px, 4vw, 56px);
  font-weight: 400;
  line-height: 1.05;
  letter-spacing: -0.01em;
  color: #1A1814;

  &::before,
  &::after {
    color: #D9442B;
  }

  &::before {
    content: '"';
  }

  &::after {
    content: '"';
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

export const Bio = styled.div``;

export const AuthorName = styled.b`
  display: block;
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: 30px;
  font-weight: 400;
  margin-top: 8px;
  color: #1A1814;
`;

export const BioText = styled.p`
  margin: 16px 0 0;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 18px;
  color: #4A453D;
  line-height: 1.55;

  &:first-of-type {
    margin-top: 16px;
  }
`;

// ---------- faq ----------
export const FAQSection = styled(Wrap)`
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: 72px;
  padding-block: 0 112px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 40px;
    padding-bottom: 72px;
  }
`;

export const FAQTitle = styled.h2`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: clamp(44px, 4.6vw, 56px);
  font-weight: 400;
  line-height: 1;
  margin: 16px 0 0;
  letter-spacing: -0.02em;
  color: #1A1814;

  em {
    font-style: italic;
    color: #D9442B;
  }

  @media (max-width: 768px) {
    font-size: 42px;
  }
`;

export const FAQList = styled.div`
  border-top: 1px solid #1A1814;
`;

export const FAQItem = styled.details`
  border-bottom: 1px solid #D2C8B8;
`;

export const FAQQuestion = styled.summary`
  list-style: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 22px 0;
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: 26px;
  font-weight: 400;
  line-height: 1.15;
  color: #1A1814;

  &::-webkit-details-marker {
    display: none;
  }

  &::after {
    content: '+';
    font-family: 'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif;
    font-size: 22px;
    font-weight: 400;
    color: #D9442B;
    transition: transform 0.2s ease;
  }

  ${FAQItem}[open] &::after {
    transform: rotate(45deg);
  }

  @media (max-width: 768px) {
    font-size: 22px;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      transition: none;
    }
  }
`;

export const FAQAnswer = styled.p`
  margin: 0 0 22px;
  color: #4A453D;
  font-family: 'Newsreader', Georgia, serif;
  font-size: 17px;
  max-width: 36em;
  line-height: 1.55;
`;

// ---------- closing ----------
export const ClosingSection = styled.section`
  background: #1A1814;
  color: #F3ECE1;
`;

export const ClosingWrap = styled(Wrap)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  gap: 40px;
  padding-block: 100px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding-block: 64px;
    gap: 32px;
  }
`;

export const ClosingTitle = styled.h2`
  font-family: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  font-size: clamp(48px, 5.8vw, 84px);
  font-weight: 400;
  line-height: 0.95;
  max-width: 11em;
  margin: 0;
  letter-spacing: -0.02em;
  color: #F3ECE1;

  em {
    font-style: italic;
    color: #D9442B;
  }

  @media (max-width: 768px) {
    font-size: 42px;
    line-height: 1;
  }
`;

export const BuyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const ClosingPrice = styled(PriceContainer)`
  margin: 0;

  ${OldPrice} {
    color: #8A8276;
  }
`;

export const LightButton = styled(PrimaryButton)`
  background: #F3ECE1;
  color: #1A1814;

  @media (max-width: 768px) {
    width: 100%;
  }
`;
